import React, { useEffect } from 'react';

import styles from './PatientGeneralInfo.module.css';
import { MdQrCode } from 'react-icons/md';
import chatIcon from '../../../assets/icons/chat-icon.svg';

import { validatePPS } from '../../../services/General';
import { getAgePatient, getTownNamePatient, getFullName, getDiagnosisPatient } from '../../../services/getPatientAtribute';
import { getUser } from '../../../services/Auth';
import { getCurrentPhase } from '../../../services/Patient';
import { getEnvironmentApiUrl } from '../../../services/General';

import PatientsContext from '../../../context/PatientsContext';
import FiltersContext from '../../../context/FiltersContext';
import ProfileContext from '../../../context/ProfileContext';
import GeneralContext from '../../../context/GeneralContext';
import { useChatContext } from '../../../context/ChatContext';

import { useContext, useRef, useState } from 'react';
import useHttp from '../../../hooks/useHttp';

import ViewFileLink from '../ViewFileLink/ViewFileLink';
import Modal from '../../UI/Modal/Modal';
import Input from '../../UI/Input/Input';
import PatientImage from '../../UI/PatientImage/PatientImage';
import WrapperSendButton from '../../UI/WrapperSendButton/WrapperSendButton';
import PatientAlertsButtons from '../PatientAlertsButtons/PatientAlertsButtons';
import GeneralGraphPatient from '../../PatientPollsPage/GeneralGraphPatient/GeneralGraphPatient';
import PatientInfoText from '../PatientInfoText/PatientInfoText';
import VIG from '../../Helpers/VIG/VIG';
import PatientQRDialog from '../../Patient/PatientQRDialog/PatientQRDialog';
import useInput from '../../../hooks/useInput';

import { PhysiologicalMonitoring } from '../../../interfaces/PhysiologicalMonitoring';
import { OptionsSelect } from '../../../interfaces/Input';

import { useTranslation } from 'react-i18next';

const PatientGeneralInfo: React.FC<{
    id: number | null,
}> = ({ id }) => {

    const patientsContext = useContext(PatientsContext);
    const { profileAttributes } = useContext(ProfileContext);
    const generalContext = useContext(GeneralContext);
    const { hasUnreadMessages, setPatientId } = useChatContext();

    const [showGenerateQr, setShowGenerateQr] = useState(false);

    const { sendRequest: putPatient } = useHttp();

    const patient = patientsContext.patients.find(patient => patient.id === id);
    const displayInfo = patient ? 'block' : undefined;

    const { t } = useTranslation();

    const inputPPS = useInput(validatePPS, (patient?.pps ?? '').toString());

    const supportResourceRef = useRef(patient?.support_resource || '');
    const commentRef = useRef(patient?.comment || '');

    useEffect(() => {
        setPatientId(id);
    }, [id]);

    const handleClickChat = () => {
        generalContext.setIsChatOpen?.(true);
    };

    const handleCommentChange = (evt: React.FormEvent) => {
        commentRef.current = (evt.target as HTMLInputElement).value;
    };

    const handleClickGenerateQr = () => setShowGenerateQr(true);

    const sendSilence = (evt: React.ChangeEvent<HTMLInputElement>) => {
        sendPhysiologicalMonitoring('SILENCE_PACT', { value: evt.target.checked ? 1 : 0 });
    };

    const sendSudAtention = (option: OptionsSelect) => {
        sendPhysiologicalMonitoring('SOUTH_ATTENTION', { sheet_parameter_option_id: option.id === 0 ? 'SOUTH_ATTENTION_HOME' : 'SOUTH_ATTENTION_HOSPITAL' });
    };

    const sendPhysiologicalMonitoring = (sheet_parameter_id: string, body: PhysiologicalMonitoring) => {
        postPhysiologicalMonitoring({
            url: getEnvironmentApiUrl() + 'physiological_monitoring/',
            method: 'POST',
            body: {
                case_id: patient?.cases[0].id,
                patient_id: id,
                deleted: false,
                modified: false,
                user_id: getUser().id,
                sheet_parameter_id,
                ...body,
                time: new Date().toISOString()
            }
        }, () => {
            patientsContext.setPatients(oldPatients => {
                return oldPatients.map(oldPatient => {
                    if (oldPatient.id !== patient?.id) return oldPatient;
                    const newPatient = { ...oldPatient };
                    if (sheet_parameter_id === 'SILENCE_PACT') newPatient.conspiracy_silence = body.value === 1;
                    if (sheet_parameter_id === 'SOUTH_ATTENTION')
                        newPatient.sud_atention = body.sheet_parameter_option_id === 'SOUTH_ATTENTION_HOME' ? t('home') :
                            body.sheet_parameter_option_id === 'SOUTH_ATTENTION_HOSPITAL' ? t('hospital') : '';
                    if (sheet_parameter_id === 'SUPPORT_RESOURCE') newPatient.support_resource = body.comment;
                    if (sheet_parameter_id === 'PPS') newPatient.pps = body.value;
                    return newPatient;
                });
            });
        });
    };

    const sendSupportResource = () => {
        sendPhysiologicalMonitoring('SUPPORT_RESOURCE', { comment: supportResourceRef.current });
    };

    const { sendRequest: postPhysiologicalMonitoring } = useHttp();
    const { sendRequest: postPatientTerminalPhase } = useHttp();

    const filtersContext = useContext(FiltersContext);
    const currentStage = patient?.pps === null || patient?.pps === undefined ? null : getCurrentPhase(filtersContext.terminalPhases, patient?.pps);

    const sendPPS = () => {
        if (!inputPPS.valueIsValid) {
            inputPPS.inputBlurHandler();
            return;
        }
        sendPhysiologicalMonitoring('PPS', { value: Number(inputPPS.value) });
        if (!patient || currentStage === undefined) return;
        const date = new Date().toISOString();

        const newStage = getCurrentPhase(filtersContext.terminalPhases, Number(inputPPS.value));
        const sendPatientTerminalPhase = () => {
            postPatientTerminalPhase({
                url: getEnvironmentApiUrl() + 'patient_terminal_phase',
                method: 'POST',
                body: {
                    patient_id: patient.id,
                    terminal_phase_id: newStage?.id,
                    start_time: date,
                    stop_time: null
                }
            });
        };
        if (currentStage === null) {
            sendPatientTerminalPhase();
            return;
        }
        if (patient && currentStage.name !== newStage?.name) {
            postPatientTerminalPhase({
                url: getEnvironmentApiUrl() + 'patient_terminal_phase',
                method: 'PUT',
                body: {
                    data: { stop_time: date },
                    filter: {
                        patient_id: patient.id,
                        terminal_phase_id: currentStage.id,
                        stop_time: null
                    }
                }
            });
            sendPatientTerminalPhase();
        }
    };

    const sendComment = () => {
        putPatient({
            url: getEnvironmentApiUrl() + 'patient/' + id,
            method: 'PUT',
            body: { comment: commentRef.current }
        });
        patientsContext.setPatients(oldPatients =>
            oldPatients.map(patientItem => {
                if (patientItem.id === id) return { ...patientItem, comment: commentRef.current };
                return patientItem;
            }));
    };

    const showImage = window.config.VERSION_IMAGES;

    const yesOrNo = (value: boolean | null | undefined) =>
        value === true ? t('yes') : value === false ? t('no') : '';

    const handleChangeSupportResource = (evt: React.FormEvent) => {
        supportResourceRef.current = (evt.target as HTMLInputElement).value;
    };

    return (
        <div className={styles.patientGeneralInfo} style={{ display: displayInfo }}>
            {patient &&
                <div className={styles.dashboardPatient}>
                    <div className={styles.fileInfoContainer}>
                        {showImage && <div className={styles.imageContainer}>
                            <PatientImage image={patient?.image} />
                            <ViewFileLink link={`/home/patients/cases/${patient.cases[0]?.id}`} />
                        </div>}
                        <div className={styles.textInfoContainer}>
                            <h3 className={styles.patientName}>
                                {getFullName(patient) + ' (' + getAgePatient(patient) + ')'}
                            </h3>
                            <PatientAlertsButtons
                                patientID={patient.id}
                                scoreParameterAlerts={patient.cases[0]?.patient_score_param_alerts}
                                inquiryAlerts={patient.cases[0]?.patient_inquiry_alerts}
                            />
                            <div className={styles.gridInfoPatient}
                                style={{
                                    gridTemplateRows: 'repeat(2, 1fr)'
                                }}
                            >
                                <PatientInfoText
                                    label={t('diagnosis')}
                                    element={getDiagnosisPatient(patient)}
                                />
                                <PatientInfoText
                                    label={t('town')}
                                    element={getTownNamePatient(patient)}
                                />
                                <PatientInfoText
                                    label={t('gender')}
                                    element={patient.sex?.name || ''}
                                />
                                <div className={styles.cellInStudy}>
                                    <span><strong>VIG: </strong></span>
                                    <VIG value={patient.VIG} />
                                </div>
                                <PatientInfoText
                                    label={t('telecare')}
                                    element={yesOrNo(patient.teleassistance)}
                                />
                                <PatientInfoText
                                    label={t('municipal_sad')}
                                    element={t(patient?.municipal ?? '-') as string}
                                />
                            </div>
                            <div className={styles.IconsAndCommentContainer}>
                                {!showImage && <div className={styles.viewFileLinkWrapper}>
                                    <ViewFileLink link={`/home/patients/cases/${patient.cases[0]?.id}`} />
                                </div>}
                                <div className={styles.iconButtons}>
                                    <button disabled={!patient.study} onClick={handleClickGenerateQr} className={styles.iconWrapper}>
                                        <MdQrCode className={styles.icon} />
                                    </button>
                                    <button className={styles.iconWrapper} onClick={handleClickChat}>
                                        <img src={chatIcon} className={styles.iconChat} alt='icon chat' />
                                        {hasUnreadMessages && <span className={styles.notificationDot}></span>}
                                    </button>
                                </div>
                                <WrapperSendButton handleClick={sendComment} className={styles.containerInputClass}>
                                    <Input
                                        label={t('comment')}
                                        defaultValue={patient?.comment || ''}
                                        onChange={handleCommentChange}
                                    />
                                </WrapperSendButton>
                                {profileAttributes?.showPadesGeneralInfo ?
                                    <WrapperSendButton handleClick={sendSupportResource} className={styles.containerInputClass}>
                                        <Input
                                            label={t('support_resource')}
                                            defaultValue={patient?.support_resource || ''}
                                            onChange={handleChangeSupportResource}
                                        />
                                    </WrapperSendButton>
                                    : null
                                }
                            </div>
                        </div>
                    </div>
                    <GeneralGraphPatient patient={patient} />
                </div>
            }
            <Modal show={showGenerateQr} setShowModal={setShowGenerateQr}>
                <PatientQRDialog
                    setShowModal={setShowGenerateQr}
                    patient={patient}
                />
            </Modal>
        </div>
    );
};

export default PatientGeneralInfo;